import type React from "react";
import { useEffect, useState } from "react";
import type { PermApiResponse } from "../../../../../interface/interfaces";
import useAdminApiHook from "../../../../../utils/useAdminApi";
import { toast } from "react-toastify";

interface PermissionCategory {
  title: string;
  permissions: string[];
}

interface CreateAdminRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateAdminRoleModal: React.FC<CreateAdminRoleModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [selectedRolePermissions, setSelectedRolePermissions] = useState<
    string[]
  >([]);
  const [permissionCategories, setPermissionCategories] = useState<
    PermissionCategory[]
  >([]);
  const [availableAdminPermissions, setAvailableAdminPermissions] = useState<
    string[]
  >([]);

  const { data, makeRequest } = useAdminApiHook<PermApiResponse>(
    "/admin/system-role",
    {
      method: "get",
    }
  );

  const { isLoading, makeRequest: createAdminRole } = useAdminApiHook(
    `/admin/system/add-role`,
    {
      method: "post",
    }
  );

  useEffect(() => {
    if (isOpen) {
      makeRequest();
    }
  }, [isOpen]);

  useEffect(() => {
    if (data?.data) {
      const adminPerms = data.data.adminRolePermissions || [];

      setAvailableAdminPermissions(adminPerms);

      // Group admin permissions by category
      const categories: Record<string, string[]> = {};
      adminPerms.forEach((permission) => {
        const category = getCategoryFromPermission(permission);
        if (!categories[category]) {
          categories[category] = [];
        }
        categories[category].push(permission);
      });

      const formattedCategories = Object.entries(categories).map(
        ([title, perms]) => ({
          title,
          permissions: perms,
        })
      );

      setPermissionCategories(formattedCategories);
    }
  }, [data]);

  const getCategoryFromPermission = (permission: string): string => {
    if (permission.includes("TRANSACTION")) return "Transactions";
    if (permission.includes("USER")) return "Users";
    if (permission.includes("CUSTOMER")) return "Customers";
    if (permission.includes("SETTLEMENT")) return "Settlements";
    if (permission.includes("ROLE")) return "Roles";
    if (permission.includes("INVITE")) return "Invitations";
    if (permission.includes("MERCHANT")) return "Merchants";
    if (permission.includes("BANK")) return "Banking";
    return "Other";
  };

  const formatPermissionLabel = (permission: string): string => {
    return permission
      .replace(/CAN_/g, "")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const handlePermissionChange = (permission: string) => {
    setSelectedRolePermissions((prev) => {
      if (prev.includes(permission)) {
        return prev.filter((p) => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  const closeModal = () => {
    onClose();
    // Reset form
    setRoleName("");
    setRoleDescription("");
    setSelectedRolePermissions([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Build payload - admin permissions go to rolePermissions
    const payload: any = {
      roleName: roleName.trim(),
      roleDescription: roleDescription.trim(),
      isAdminRole: true,
      isSystemRole: false,
      isCustomRole: true,
    };

    // Only add rolePermissions if there are selected permissions
    if (selectedRolePermissions.length > 0) {
      payload.rolePermissions = selectedRolePermissions;
    }

    console.log("Creating admin role with payload:", payload);

    const response = await createAdminRole(payload);

    if (response?.status === 200 || response?.status === 201) {
      toast("Admin role created successfully");

      setTimeout(() => {
        closeModal();
        window.location.reload();
      }, 3000);

      // You might want to refresh the table data here or show success message
    } else {
      console.error("Creation failed");
    }
  };

  const isFormValid =
    roleName.trim() &&
    roleDescription.trim() &&
    selectedRolePermissions.length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Create Admin Role
          </h2>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Role Name */}
            <div>
              <label
                htmlFor="roleName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Role Name *
              </label>
              <input
                type="text"
                id="roleName"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="Enter role name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Role Description */}
            <div>
              <label
                htmlFor="roleDescription"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Role Description *
              </label>
              <textarea
                id="roleDescription"
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.target.value)}
                placeholder="Enter role description"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Permissions */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4">
                Select Admin Permissions ({selectedRolePermissions.length})
              </h3>

              {availableAdminPermissions.length > 0 ? (
                <div className="max-h-96 overflow-y-auto space-y-4">
                  {permissionCategories.map((category) => {
                    const availableInCategory = category.permissions.filter(
                      (perm) => availableAdminPermissions.includes(perm)
                    );

                    if (availableInCategory.length === 0) return null;

                    return (
                      <div key={category.title} className="space-y-2">
                        <h4 className="font-medium text-gray-700 text-sm">
                          {category.title}
                        </h4>
                        <div className="space-y-1 pl-2">
                          {availableInCategory.map((permission) => (
                            <div key={permission} className="flex items-center">
                              <input
                                type="checkbox"
                                id={permission}
                                checked={selectedRolePermissions.includes(
                                  permission
                                )}
                                onChange={() =>
                                  handlePermissionChange(permission)
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor={permission}
                                className="ml-2 text-sm text-gray-700"
                              >
                                {formatPermissionLabel(permission)}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Loading admin permissions...</p>
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !isFormValid}
              className={`px-4 py-2 text-white rounded-md transition-colors flex items-center justify-center ${
                isLoading || !isFormValid
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating...
                </>
              ) : (
                "Create Admin Role"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAdminRoleModal;
